/*
Copyright (C) 2026  Tech9IQ
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/**
 * /login/tech9iq: the page Tech9IQ opens Swift on (docs/swift-sso.md). It
 * shows only for a moment: the handoff is exchanged for a Swift session, and
 * the portal then loads signed in. If it doesn't go through, it says why and
 * waits for a click, rather than bouncing between the two sites.
 */

import React from "react";
import styled from "styled-components";

import configLoader from "@src/utils/Config";
import userStore from "@src/stores/UserStore";
import ObjectUtils from "@src/utils/ObjectUtils";
import SsoSource, { readHandoff, ssoOptions } from "@src/sources/SsoSource";
import { hidePreloader } from "@src/utils/preloader";
import AuthLayout, { AuthPreloader, CardHead } from "@src/components/modules/LoginModule/AuthLayout";

/*
 * Was a lone 420px card; now signin.tech9iq.com's look: the preloader while it signs in, and the
 * split layout's card (AuthLayout) if it doesn't go through. The old styles, kept for reference:
const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  font-family: Montserrat, system-ui, sans-serif;
`;
const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: var(--t9-bg-surface);
  border: 1px solid var(--t9-border);
  border-radius: 12px;
  padding: 24px;
  color: var(--t9-text-muted);
  font-size: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
`;
const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: var(--t9-text);
  margin: 0 0 12px;
`;
const ErrorText = styled.p`
  color: var(--t9-danger-700);
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;
const Primary = styled.a`
  background: var(--t9-primary);
  color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  text-decoration: none;
  font-weight: 500;
`;
const Secondary = styled.a`
  color: var(--t9-text-muted);
  border-radius: 8px;
  padding: 8px 12px;
  text-decoration: none;
`;
*/
const ErrorText = styled.p`
  margin: 0 0 20px;
  padding: 12px 16px;
  border: 1px solid var(--t9-danger-200);
  border-radius: 8px;
  background: var(--t9-danger-50);
  color: var(--t9-danger-700);
  font-size: 14px;
  line-height: 20px;
`;
const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const action = `
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  text-decoration: none;
`;
const Primary = styled.a`
  ${action}
  background: var(--t9-primary);
  color: #fff;
  &:hover {
    background: var(--t9-primary-hover);
  }
`;
const Secondary = styled.a`
  ${action}
  background: var(--t9-bg-surface);
  border-color: var(--t9-border-control);
  color: var(--t9-text-body);
  &:hover {
    background: var(--t9-bg-muted);
  }
`;

type State = { error: string | null };

type Props = {
  /** For tests: where to go once signed in. */
  onSignedIn?: (next: string) => void;
};

class SsoPage extends React.Component<Props, State> {
  state: State = { error: null };

  async componentDidMount() {
    const handoff = readHandoff(window.location.hash);
    // Out of the address bar and the history at once, before anything else runs.
    window.history.replaceState(null, "", window.location.pathname);
    if (!handoff) {
      hidePreloader();
      this.setState({ error: "This page is opened from Tech9IQ, with a sign-in link. Open SwiftIQ from Tech9IQ again." });
      return;
    }
    // The app checks for an existing session as it starts; wait for that to
    // settle, so its "signed out" answer can't clear the new session's cookies.
    await ObjectUtils.waitFor(() => !userStore.loading, { timeoutMs: 10000, intervalMs: 100, silent: true });
    try {
      const { next } = await SsoSource.exchange(handoff);
      if (this.props.onSignedIn) this.props.onSignedIn(next);
      else window.location.replace(next);
    } catch (err: any) {
      // Up until now index.html's preloader covered this page; it says why here.
      hidePreloader();
      this.setState({ error: err && err.message ? err.message : "Signing in from Tech9IQ did not go through." });
    }
  }

  render() {
    const home = configLoader.config ? configLoader.config.tech9iqHomeUrl : "";
    const homeLabel = (configLoader.config && configLoader.config.tech9iqHomeLabel) || "Back to Ops Console";
    if (!this.state.error) {
      // index.html's preloader covers this page until it hides; this is the same, underneath.
      return <AuthPreloader text="Signing you in from Tech9IQ…" />;
    }
    return (
      <AuthLayout>
        <CardHead title="Sign in to SwiftIQ" sub="Signing in from Tech9IQ did not go through." />
        <ErrorText role="alert">{this.state.error}</ErrorText>
        <Actions>
          <Primary href={ssoOptions().startUrl}>Try again</Primary>
          {home ? <Secondary href={home}>{homeLabel}</Secondary> : null}
        </Actions>
      </AuthLayout>
    );
    /* The lone-card render this replaced:
    return (
      <Wrapper>
        <Card>
          <Title>SwiftIQ</Title>
          {this.state.error ? (
            <div>
              <ErrorText role="alert">{this.state.error}</ErrorText>
              <Actions>
                <Primary href={ssoOptions().startUrl}>Try again</Primary>
                {home ? <Secondary href={home}>{homeLabel}</Secondary> : null}
              </Actions>
            </div>
          ) : (
            <p>Signing you in from Tech9IQ…</p>
          )}
        </Card>
      </Wrapper>
    );
    */
  }
}

export default SsoPage;
